/**
 * Voice2Evidence™ — Stream Commissioning Brief PDF (generic, per-stream)
 *
 * Renders any V2E stream brief (L1 SML, L2 PRO, L3 advisory, L4 RWD/RWE,
 * L5 Regulatory/HTA) as a downloadable PDF using a single parametrized
 * template. Each brief declares its own `stream_label`, `title`, source
 * universe, evidence axes, mandatory mapping, per-insight fields,
 * exclusions, output format, ethics floor, and downstream use.
 */
import {
  Document, Page, Text, View, StyleSheet, Svg, Line, pdf,
} from "@react-pdf/renderer";

const RUST = "#C84C21";
const INK  = "#0F1825";
const MUTE = "#5C6470";
const SOFT = "#A09680";
const CREAM = "#FAF7F1";
const SAGE  = "#3F6D55";

const s = StyleSheet.create({
  page: {
    paddingTop: 56,
    paddingBottom: 64,
    paddingHorizontal: 56,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: INK,
    lineHeight: 1.5,
  },
  /* Brand chrome */
  brandRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 6 },
  brandLeft:  { flexDirection: "column" },
  brandRight: { flexDirection: "column", alignItems: "flex-end" },
  wordmark:   { fontSize: 11, color: RUST, letterSpacing: 3, fontFamily: "Helvetica-Bold" },
  tmMark:     { fontSize: 6.5, color: RUST, fontFamily: "Helvetica-Bold", marginLeft: 1, paddingTop: 1 },
  tagline:    { fontSize: 8, color: SOFT, letterSpacing: 2, marginTop: 4, marginLeft: 16 },
  reportKind: { fontSize: 8, color: MUTE, letterSpacing: 2, textTransform: "uppercase" },
  reportRef:  { fontSize: 8, color: SOFT, marginTop: 2 },
  rule:       { marginTop: 10, marginBottom: 22, height: 1, backgroundColor: RUST, width: 32 },

  /* Cover */
  eyebrow:    { fontSize: 9, color: RUST, letterSpacing: 2.5, textTransform: "uppercase", marginBottom: 8 },
  title:      { fontSize: 24, color: INK, fontFamily: "Times-Roman", lineHeight: 1.15, marginBottom: 10 },
  subtitle:   { fontSize: 11, color: MUTE, lineHeight: 1.55, marginBottom: 22, maxWidth: 460 },

  /* Cover metadata grid */
  metaGrid:   { marginTop: 14, marginBottom: 22, flexDirection: "row", flexWrap: "wrap" },
  metaCell:   { width: "50%", marginBottom: 14, paddingRight: 12 },
  metaLabel:  { fontSize: 7.5, color: SOFT, letterSpacing: 1.8, textTransform: "uppercase", marginBottom: 3 },
  metaValue:  { fontSize: 11, color: INK },

  /* Sections */
  section:    { marginTop: 18, marginBottom: 4 },
  sectionEy:  { fontSize: 8, color: RUST, letterSpacing: 2.5, textTransform: "uppercase", marginBottom: 5 },
  sectionTtl: { fontSize: 14, color: INK, fontFamily: "Times-Roman", marginBottom: 10 },
  paragraph:  { fontSize: 10.5, color: INK, lineHeight: 1.55, marginBottom: 10 },
  bodyMute:   { fontSize: 10, color: MUTE, lineHeight: 1.55, marginBottom: 8 },

  /* List items */
  listItem:   { flexDirection: "row", marginBottom: 6, alignItems: "flex-start" },
  bulletNum:  { width: 22, fontSize: 8, color: RUST, letterSpacing: 1.5, paddingTop: 2 },
  itemBody:   { flex: 1, fontSize: 10, color: INK, lineHeight: 1.45 },

  /* Two-column lists for dense taxonomy pages */
  twoCol:     { flexDirection: "row", flexWrap: "wrap" },
  col:        { width: "50%", paddingRight: 8 },

  /* Callout panels */
  callout:    { marginTop: 14, padding: 12, backgroundColor: CREAM, borderLeft: 3, borderLeftColor: RUST },
  calloutLbl: { fontSize: 7.5, color: RUST, letterSpacing: 1.8, marginBottom: 5, fontFamily: "Helvetica-Bold" },
  calloutBody:{ fontSize: 10, color: INK, lineHeight: 1.55 },

  /* Ethics callout — sage accent */
  ethics:     { marginTop: 14, padding: 12, backgroundColor: "#EAF3EC", borderLeft: 3, borderLeftColor: SAGE },
  ethicsLbl:  { fontSize: 7.5, color: SAGE, letterSpacing: 1.8, marginBottom: 5, fontFamily: "Helvetica-Bold" },
  ethicsBody: { fontSize: 10, color: "#2E4A38", lineHeight: 1.55 },

  /* Footer */
  footer: {
    position: "absolute",
    bottom: 28,
    left: 56,
    right: 56,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#E8E1D2",
  },
  footerLeft:  { fontSize: 7, color: SOFT, letterSpacing: 1.4 },
  footerRight: { fontSize: 7, color: SOFT, letterSpacing: 1.4 },
});

function AccentLine() {
  return (
    <Svg width="32" height="2" style={{ marginTop: 10, marginBottom: 22 }}>
      <Line x1="0" y1="1" x2="32" y2="1" stroke={RUST} strokeWidth="2" />
    </Svg>
  );
}

function BrandHeader({ reportRef, streamCode, briefKind }) {
  return (
    <View fixed>
      <View style={s.brandRow}>
        <View style={s.brandLeft}>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <Svg width="10" height="10" style={{ marginRight: 6 }}>
              <Line x1="0" y1="5" x2="10" y2="5" stroke={RUST} strokeWidth="3" />
            </Svg>
            <Text style={s.wordmark}>VOICE2EVIDENCE</Text>
            <Text style={s.tmMark}>TM</Text>
          </View>
          <Text style={s.tagline}>by MarksMan Healthcare</Text>
        </View>
        <View style={s.brandRight}>
          <Text style={s.reportKind}>{(briefKind || "STREAM BRIEF")} · {streamCode || ""}</Text>
          {reportRef ? <Text style={s.reportRef}>{reportRef}</Text> : null}
        </View>
      </View>
      <AccentLine />
    </View>
  );
}

function PageFooter({ eng, briefKind }) {
  return (
    <View style={s.footer} fixed>
      <Text style={s.footerLeft}>
        Voice2Evidence · {briefKind || "Stream Brief"} · {eng?.brand || ""} · {eng?.client_label || ""}
      </Text>
      <Text
        style={s.footerRight}
        render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
      />
    </View>
  );
}

function MetaCell({ label, value }) {
  return (
    <View style={s.metaCell}>
      <Text style={s.metaLabel}>{label}</Text>
      <Text style={s.metaValue}>{value || "—"}</Text>
    </View>
  );
}

function SectionHeader({ eyebrow, title }) {
  return (
    <View style={s.section}>
      {eyebrow ? <Text style={s.sectionEy}>{eyebrow}</Text> : null}
      <Text style={s.sectionTtl}>{title}</Text>
    </View>
  );
}

function NumberedItem({ n, children }) {
  return (
    <View style={s.listItem} wrap={false}>
      <Text style={s.bulletNum}>{String(n).padStart(2, "0")}</Text>
      <Text style={s.itemBody}>{children}</Text>
    </View>
  );
}

/* Two-column list helper for dense taxonomy renders */
function TwoColList({ items }) {
  const mid = Math.ceil(items.length / 2);
  const left = items.slice(0, mid);
  const right = items.slice(mid);
  return (
    <View style={s.twoCol}>
      <View style={s.col}>
        {left.map((it, i) => <NumberedItem key={i} n={i + 1}>{it}</NumberedItem>)}
      </View>
      <View style={s.col}>
        {right.map((it, i) => <NumberedItem key={i} n={mid + i + 1}>{it}</NumberedItem>)}
      </View>
    </View>
  );
}

/* ============================================================
 * GENERIC STREAM BRIEF REPORT
 * ============================================================
 * Takes the engagement document and the stream code ("L1" .. "L5"),
 * reads `eng.stream_briefs[streamCode]`, and renders the brief as a
 * branded PDF. Sections are conditionally rendered — a brief that
 * omits e.g. `stratification` or `validated_instruments` simply skips
 * that section. Page numbers in section eyebrows are auto-assigned.
 * ============================================================ */
export function StreamBriefReport({ eng, streamCode = "L1" }) {
  const brief = eng?.stream_briefs?.[streamCode] || {};
  const today = new Date().toISOString().slice(0, 10);

  const sections = [];
  if ((brief.sources || []).length) sections.push({
    eyebrow: "Source universe", title: "Sources the lead must crawl.",
    body: "Coverage MUST include every named source. Where the lead substitutes a source, the substitution must be flagged in the methodology audit.",
    items: brief.sources, twoCol: true,
  });
  if ((brief.evidence_axes || brief.quantify_axes || []).length) sections.push({
    eyebrow: "Evidence axes", title: "What the lead must identify and extract.",
    body: "Each axis is mandatory. Returned dataset is rejected if any axis is missing without a methodological explanation.",
    items: brief.evidence_axes || brief.quantify_axes, twoCol: true,
  });
  if ((brief.validated_instruments || []).length) sections.push({
    eyebrow: "Validated instruments", title: "Instruments the lead must search and report against.",
    body: "Every applicable instrument must be searched and reported with MCID / responsiveness anchor where published.",
    items: brief.validated_instruments, twoCol: true,
  });
  if ((brief.stratification || []).length) sections.push({
    eyebrow: "Stratification", title: "How the lead must segment every finding.",
    body: "Each insight must be reported per stratification dimension where attributable.",
    items: brief.stratification, twoCol: true,
  });
  if (brief.mandatory_v2e_mapping) sections.push({
    eyebrow: "Mandatory V2E mapping",
    title: "Every finding must be classified to one V2E theme and one sub-theme.",
    body: brief.mandatory_v2e_mapping, items: null,
  });
  const piFields = brief.per_insight_fields || brief.per_study_fields || [];
  if (piFields.length) sections.push({
    eyebrow: brief.per_study_fields ? "Per-study fields" : "Per-insight fields",
    title: "What the lead must return for every record.",
    body: "Every returned record must carry every field. Missing values must be explicitly marked NA.",
    items: piFields, twoCol: false,
  });
  if ((brief.exclusions || []).length) sections.push({
    eyebrow: "Exclusions", title: "What the lead must NOT include.",
    body: null, items: brief.exclusions, twoCol: false,
  });
  if ((brief.output_format || []).length) sections.push({
    eyebrow: "Output format", title: "What the deliverable must look like.",
    body: null, items: brief.output_format, twoCol: false,
  });

  const briefKind = brief.title ? brief.title.toUpperCase().split(" ").slice(0, 3).join(" ") + " BRIEF" : "STREAM BRIEF";

  return (
    <Document>
      {/* Cover */}
      <Page size="A4" style={s.page}>
        <BrandHeader reportRef={brief.reference} streamCode={streamCode} briefKind={briefKind} />

        <Text style={s.eyebrow}>{streamCode} · {brief.stream_label || ""} — Commissioning Artefact</Text>
        <Text style={s.title}>
          {brief.title || "Stream Commissioning Brief"} — {eng?.brand || ""}
          {"\n"}
          <Text style={{ fontFamily: "Times-Italic", color: MUTE }}>
            patient voice, scoped for evidence.
          </Text>
        </Text>
        <Text style={s.subtitle}>
          {brief.objective || "Locked-scope brief issued to the commissioned lead. The output of this brief becomes the " + streamCode + " stream of the Voice2Evidence engagement."}
        </Text>

        <View style={s.metaGrid}>
          <MetaCell label="Engagement" value={`${eng?.client_label || ""} · ${eng?.brand || ""}`} />
          <MetaCell label="Indication" value={brief.indication} />
          <MetaCell label="Reference" value={brief.reference} />
          <MetaCell label="Issued by" value={brief.issued_by} />
          <MetaCell label="Issued to" value={brief.issued_to} />
          <MetaCell label="Issue date" value={brief.issue_date || today} />
          <MetaCell label="Delivery window" value={brief.delivery_window} />
          <MetaCell label="Lookback period" value={brief.lookback_period} />
        </View>

        <PageFooter eng={eng} briefKind={briefKind} />
      </Page>

      {/* One body page per section */}
      {sections.map((sec, si) => (
        <Page key={si} size="A4" style={s.page}>
          <BrandHeader reportRef={brief.reference} streamCode={streamCode} briefKind={briefKind} />
          <SectionHeader
            eyebrow={`${String(si + 1).padStart(2, "0")} · ${sec.eyebrow}`}
            title={sec.title}
          />
          {sec.body && <Text style={s.bodyMute}>{sec.body}</Text>}
          {sec.items && (sec.twoCol
            ? <TwoColList items={sec.items} />
            : sec.items.map((it, i) => <NumberedItem key={i} n={i + 1}>{it}</NumberedItem>)
          )}
          <PageFooter eng={eng} briefKind={briefKind} />
        </Page>
      ))}

      {/* Ethics floor + downstream use + decision questions */}
      <Page size="A4" style={s.page}>
        <BrandHeader reportRef={brief.reference} streamCode={streamCode} briefKind={briefKind} />
        {(brief.ethics_floor || []).length > 0 && (
          <View style={s.ethics}>
            <Text style={s.ethicsLbl}>ETHICS FLOOR — NON-NEGOTIABLE</Text>
            {brief.ethics_floor.map((it, i) =>
              <Text key={i} style={[s.ethicsBody, { marginBottom: 4 }]}>· {it}</Text>
            )}
          </View>
        )}
        {brief.downstream_use && (
          <View style={s.callout}>
            <Text style={s.calloutLbl}>DOWNSTREAM USE</Text>
            <Text style={s.calloutBody}>{brief.downstream_use}</Text>
          </View>
        )}
        {(eng?.decision_questions || []).length > 0 && (
          <>
            <SectionHeader
              eyebrow="Decision questions"
              title="The strategic questions this stream is engineered to inform."
            />
            <Text style={s.bodyMute}>
              Findings that do not advance any of these are flagged but de-prioritised.
            </Text>
            {eng.decision_questions.map((q, i) =>
              <NumberedItem key={i} n={i + 1}>{q}</NumberedItem>
            )}
          </>
        )}
        <View style={s.callout}>
          <Text style={s.calloutLbl}>OWNERSHIP</Text>
          <Text style={s.calloutBody}>
            This brief is the property of {brief.issued_by || "Voice2Evidence · MarksMan Healthcare"}
            {" "}on behalf of {eng?.client_label || "the Sponsor"}. It may not be reproduced,
            redistributed, or referenced in any external document without
            written authorisation.
          </Text>
        </View>
        <PageFooter eng={eng} briefKind={briefKind} />
      </Page>
    </Document>
  );
}

/* Backwards-compatible alias for the legacy import name. */
export const TalkwalkerBriefReport = StreamBriefReport;

/* ============================================================
 * Helper — generate + download
 * ============================================================ */
export async function downloadStreamBrief(eng, streamCode, filename) {
  const blob = await pdf(<StreamBriefReport eng={eng} streamCode={streamCode} />).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
